import { useCallback, useState } from "react";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import { handleErrorSaveDataResponse, usePassportService } from "../../../../../@vendor/utils";
import CartRepository from "../../../../../repositories/CartRepository";
import { useTranslation } from "react-i18next";
import { BookingAssessmentForm } from "../../../providers/booking-assessment.provider";
import { AssessmentSchedule } from "../../../../../models/Assessment";
import { useAppSelector } from "../../../../../stores/hooks";
import { useCheckoutBookingAssessment } from "../../../providers/checkout-booking-assessment.provider";

interface UseBookingAssessmentProps {
  formValues: BookingAssessmentForm;
  assessmentSchedules?: AssessmentSchedule[];
}

export const useCartBookingAssessment = ({
  formValues,
  assessmentSchedules = [],
}: UseBookingAssessmentProps) => {
  const { t } = useTranslation();
  const { refetchUser } = usePassportService(true);
  const { account } = useAppSelector((state) => state.system);
  const [cartLoading, setCartLoading] = useState(false);
  const navigate = useNavigate();
  const {onChangeBookingData} = useCheckoutBookingAssessment();

  const isLoggedIn = !!account?.id;

  const handleBookingCart = useCallback(
    async (type: 'cart' | 'checkout' | 'guest') => {
      if (!formValues.patient || !formValues.assessment) {
        console.error("Missing required data for cart booking");
        return;
      }

      setCartLoading(true);

      try {
        // For guest users (not logged in), go directly to checkout
        if (type === 'guest' || (!isLoggedIn && type === 'checkout')) {
          // Navigate to guest checkout page - data will be read from context
          onChangeBookingData(formValues);
          navigate('/checkout');
          return;
        }

        // For logged in users, use existing cart system
        const payload: any = {
          customer_id: formValues.patient.id,
          assessment_id: formValues.assessment.id,
        };

        if (formValues.assessment.has_schedule) {
          const isMultiSession = formValues.schedule_ids.length > 1;
          
          if (isMultiSession) {
            payload.schedules = formValues.time_ids.map((timeId, index) => ({
              id: timeId,
              duration: _.get(assessmentSchedules, `[${index}].session_duration`, 60),
            }));
            payload.is_package = true;
          } else {
            payload.schedule_id = formValues.time_ids[0];
            payload.duration = _.get(assessmentSchedules, "[0].session_duration", 60);
          }
        }

        const response = await CartRepository.create(payload);
        const { data: responseData } = response.data;

        await refetchUser();
        
        if (type === 'cart') {
          navigate(`/cart?carts=${responseData.id}`);
        } else {
          navigate(`/cart?step=payment&carts=${responseData.id}`);
        }
      } catch (error: any) {
        handleErrorSaveDataResponse(t, error);
        console.error("Cart booking error:", error);
      } finally {
        setCartLoading(false);
      }
    },
    [formValues, assessmentSchedules, navigate, refetchUser, t, isLoggedIn]
  );

  return { cartLoading, handleBookingCart };
};
